import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { content, resolveFeatured } from '../data/content';
import { links } from '../data/links';
import { gsap, useGSAP } from '../lib/gsap';
import { useReveal } from '../hooks/useReveal';
import StatCounter from '../components/StatCounter';
import ProjectCard from '../components/ProjectCard';

const Home = ({ language }) => {
    const t = content[language];
    const root = useRef(null);
    const typeRef = useRef(null);
    const featured = resolveFeatured(t);

    // Generic mount + scroll reveals for this page.
    useReveal(root);

    // Real, theme-aware typewriter for the rotating titles (replaces the old
    // external readme-typing-svg image). Rebuilds when the language changes.
    useGSAP(() => {
        const el = typeRef.current;
        if (!el) return;
        const titles = t.hero.titles;
        const mm = gsap.matchMedia();

        mm.add('(prefers-reduced-motion: reduce)', () => {
            el.textContent = titles[0];
        });

        mm.add('(prefers-reduced-motion: no-preference)', () => {
            const tl = gsap.timeline({ repeat: -1, defaults: { ease: 'none' } });
            titles.forEach((title) => {
                const o = { n: 0 };
                tl.to(o, {
                    n: title.length,
                    duration: title.length * 0.08,
                    onUpdate: () => { el.textContent = title.slice(0, Math.round(o.n)); },
                })
                    .to(o, { n: title.length, duration: 1.2 }) // hold
                    .to(o, {
                        n: 0,
                        duration: title.length * 0.04,
                        onUpdate: () => { el.textContent = title.slice(0, Math.round(o.n)); },
                    });
            });
            return () => tl.kill();
        });

        return () => mm.revert();
    }, { dependencies: [language], revertOnUpdate: true, scope: root });

    return (
        <div ref={root} className="min-h-screen pt-20 pb-10">
            {/* Hero Section */}
            <section className="container mx-auto px-4 pt-10 pb-6 flex flex-col items-center text-center">
                <div className="reveal mb-6">
                    <div className="w-40 h-40 mx-auto rounded-full overflow-hidden border-4 border-blue-500 shadow-xl mb-6">
                        <img src={`${process.env.PUBLIC_URL}/ben_logo.jpg`} alt="Shun-Pin (Ben) Yeh" className="w-full h-full object-cover" />
                    </div>
                    <h1 className="text-4xl md:text-6xl font-bold text-gray-800 dark:text-white mb-4">
                        {t.hero.greeting}
                    </h1>
                    <div className="h-12 mb-2 flex items-center justify-center text-xl md:text-2xl font-medium">
                        <span ref={typeRef} className="text-blue-600 dark:text-blue-400"></span>
                        <span
                            className="ml-1 inline-block w-0.5 h-6 bg-blue-600 dark:bg-blue-400 animate-pulse align-middle"
                            aria-hidden="true"
                        ></span>
                        {/* Static copy for screen readers / SEO */}
                        <span className="sr-only">{t.hero.titles.join(', ')}</span>
                    </div>
                    <p className="text-base md:text-lg text-gray-600 dark:text-gray-300">
                        {t.hero.tagline}
                    </p>
                </div>

                {/* Primary CTAs */}
                <div className="reveal flex flex-wrap justify-center gap-4 mb-5">
                    <Link
                        to="/projects"
                        className="px-6 py-3 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors shadow-lg flex items-center gap-2"
                    >
                        <i className="fas fa-folder-open"></i> {t.hero.actions.viewProjects}
                    </Link>
                    <a
                        href={links.resume}
                        target="_blank"
                        rel="noreferrer"
                        className="px-6 py-3 bg-gradient-to-r from-blue-600 to-cyan-500 text-white rounded-full hover:opacity-90 transition-opacity shadow-lg flex items-center gap-2"
                    >
                        <i className="fas fa-file-arrow-down"></i> {t.hero.actions.resume}
                    </a>
                </div>

                {/* Secondary social links */}
                <div className="reveal flex justify-center gap-3">
                    <a href={`mailto:${links.email}`} aria-label="Email" className="w-11 h-11 flex items-center justify-center rounded-full border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:border-blue-500 hover:text-blue-500 transition-colors">
                        <i className="fas fa-envelope"></i>
                    </a>
                    <a href={links.linkedin} target="_blank" rel="noreferrer" aria-label="LinkedIn" className="w-11 h-11 flex items-center justify-center rounded-full border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:border-blue-500 hover:text-blue-500 transition-colors">
                        <i className="fab fa-linkedin-in"></i>
                    </a>
                    <a href={links.github} target="_blank" rel="noreferrer" aria-label="GitHub" className="w-11 h-11 flex items-center justify-center rounded-full border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:border-blue-500 hover:text-blue-500 transition-colors">
                        <i className="fab fa-github"></i>
                    </a>
                </div>
            </section>

            {/* About Summary */}
            <section className="container mx-auto px-4 py-16">
                <div className="reveal-card max-w-4xl mx-auto bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 md:p-12">
                    <h2 className="text-3xl font-bold mb-8 text-gray-800 dark:text-white flex items-center gap-3">
                        <span className="text-2xl">🌱</span> {t.about.title}
                    </h2>
                    <div className="prose dark:prose-invert max-w-none">
                        <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed mb-6">
                            {t.about.description}
                        </p>
                        <div className="grid md:grid-cols-2 gap-6 mt-8">
                            <div className="space-y-4">
                                <h3 className="font-bold text-xl text-blue-600 dark:text-blue-400">
                                    {t.about.education.title}
                                </h3>
                                <ul className="space-y-2 text-gray-600 dark:text-gray-400">
                                    {t.about.education.items.map((item, index) => (
                                        <li key={index} className="flex items-start gap-2">
                                            <span className="mt-1">🎓</span>
                                            <span>{item}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            <div className="space-y-4">
                                <h3 className="font-bold text-xl text-blue-600 dark:text-blue-400">
                                    {t.about.focus.title}
                                </h3>
                                <div className="flex flex-wrap gap-2">
                                    {t.about.focus.items.map((skill) => (
                                        <span key={skill} className="px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-300 rounded-full text-sm">
                                            {skill}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Stats */}
            <section className="container mx-auto px-4 py-8">
                <h2 className="reveal text-3xl font-bold mb-8 text-center text-gray-800 dark:text-white">
                    {t.stats.title}
                </h2>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
                    {t.stats.items.map((s, index) => (
                        <StatCounter
                            key={index}
                            value={s.value}
                            decimals={s.decimals}
                            prefix={s.prefix}
                            suffix={s.suffix}
                            label={s.label}
                            icon={s.icon}
                        />
                    ))}
                </div>
            </section>

            {/* Skills */}
            <section className="container mx-auto px-4 py-12">
                <h2 className="reveal text-3xl font-bold mb-8 text-center text-gray-800 dark:text-white">
                    {t.skills.title}
                </h2>
                <div className="max-w-5xl mx-auto grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {t.skills.groups.map((group, index) => (
                        <div key={index} className="reveal-card bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
                            <h3 className="font-bold text-lg text-blue-600 dark:text-blue-400 mb-4">
                                {group.label}
                            </h3>
                            <div className="flex flex-wrap gap-2">
                                {group.items.map((item) => (
                                    <span key={item} className="px-3 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full text-sm">
                                        {item}
                                    </span>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* Featured Work: selected by id in content.js (featuredWork) */}
            <section className="container mx-auto px-4 py-12">
                <h2 className="reveal text-3xl font-bold mb-8 text-center text-gray-800 dark:text-white">
                    {t.featured.title}
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
                    {featured.map(({ type, item }) =>
                        type === 'experience' ? (
                            <ProjectCard
                                key={item.id}
                                title={item.headline}
                                subtitle={`${item.title} · ${item.org}`}
                                desc={item.details.join(language === 'zh' ? '' : ' ')}
                                tech={item.tech}
                                status={item.period}
                                icon={item.icon}
                                color={item.color}
                                to="/experience"
                                actionLabel={t.featured.actions.experience}
                                moreLabel={t.projects.actions.more}
                                lessLabel={t.projects.actions.less}
                            />
                        ) : (
                            <ProjectCard
                                key={item.id}
                                title={item.title}
                                subtitle={item.subtitle}
                                desc={item.desc}
                                tech={item.tech}
                                status={item.status}
                                state={item.state}
                                icon={item.icon}
                                color={item.color}
                                href={item.link || undefined}
                                actionLabel={t.featured.actions.project}
                                moreLabel={t.projects.actions.more}
                                lessLabel={t.projects.actions.less}
                            />
                        )
                    )}
                </div>
            </section>
        </div>
    );
};

export default Home;
